import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, type Tenant, type components } from '@akillidernek/api-client'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Dialog,
  Field,
  Input,
  useToast,
  type Column,
} from '@akillidernek/ui'
import { api } from '../api'
import { setAccessToken } from '../auth-store'
import { setTenantSlug } from '../tenant-store'

type Saglik = components['schemas']['TenantHealthRead']
type Plan = components['schemas']['PlanRead']
type Duyuru = components['schemas']['AnnouncementRead']

const TONE = {
  trial: 'info',
  active: 'success',
  past_due: 'warning',
  suspended: 'danger',
  closed: 'neutral',
} as const

const LABEL = {
  trial: 'Deneme',
  active: 'Aktif',
  past_due: 'Ödeme gecikti',
  suspended: 'Askıda',
  closed: 'Kapalı',
} as const

/**
 * "Olarak görüntüle" — destek personeli derneği kullanıcının gördüğü gibi
 * görür (YES-86). Alınan jeton tek derneğe sabitli ve kısa ömürlü; panelde
 * kalıcı bir uyarı bandı görünür.
 */
function OlarakGoruntule({ tenant }: { tenant: Tenant }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [acik, setAcik] = useState(false)
  const [gonderiliyor, setGonderiliyor] = useState(false)

  async function baslat(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const gerekce = String(new FormData(event.currentTarget).get('reason') ?? '')
    setGonderiliyor(true)
    try {
      const { data } = await api.POST('/api/v1/platform/impersonate', {
        body: { tenant_slug: tenant.slug, reason: gerekce },
      })
      if (data) {
        setAccessToken(data.access_token)
        setTenantSlug(tenant.slug)
        // Kimlik değişti: önceki oturumun verisi önbellekte kalmamalı.
        // `clear()` DEĞİL `resetQueries()`: clear cache'i boşaltır ama monte
        // sorguları yeniden çekmez; uyarı bandı görünmez kalırdı.
        await queryClient.resetQueries()
        setAcik(false)
      }
    } catch (error) {
      toast({
        title: 'Destek oturumu başlatılamadı',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      })
    } finally {
      setGonderiliyor(false)
    }
  }

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setAcik(true)}>
        Olarak görüntüle
      </Button>
      <Dialog
        open={acik}
        onOpenChange={setAcik}
        title={`${tenant.name} olarak görüntüle`}
        description="Bu oturum yalnızca bu derneğe açılır ve yaptığınız her işlem denetim kaydına adınızla düşer."
        footer={
          <>
            <Button variant="secondary" onClick={() => setAcik(false)}>
              Vazgeç
            </Button>
            <Button type="submit" form="destek-formu" disabled={gonderiliyor}>
              {gonderiliyor ? 'Açılıyor…' : 'Başlat'}
            </Button>
          </>
        }
      >
        <form id="destek-formu" onSubmit={baslat}>
          <Field label="Gerekçe" required hint="Destek talebi numarası ya da kısa açıklama.">
            {({ id, invalid, required }) => (
              <Input id={id} name="reason" invalid={invalid} required={required} minLength={3} />
            )}
          </Field>
        </form>
      </Dialog>
    </>
  )
}

/** Plan değişikliği — YES-169. Onboarding'deki seçimle aynı servis. */
function PlanDegistir({ tenant, planlar }: { tenant: Saglik; planlar: Plan[] }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [acik, setAcik] = useState(false)

  const ata = useMutation({
    mutationFn: async (form: FormData) => {
      await api.PUT('/api/v1/platform/tenants/{tenant_slug}/plan', {
        params: { path: { tenant_slug: tenant.slug } },
        body: {
          plan_code: String(form.get('plan_code')),
          billing_cycle: String(form.get('billing_cycle')) as 'monthly' | 'yearly',
        },
      })
    },
    onSuccess: async () => {
      setAcik(false)
      toast({ title: `${tenant.name} planı güncellendi`, tone: 'success' })
      await queryClient.invalidateQueries({ queryKey: ['tenants-health'] })
    },
    onError: (error) =>
      toast({
        title: 'Plan değiştirilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setAcik(true)}>
        Plan
      </Button>
      <Dialog
        open={acik}
        onOpenChange={setAcik}
        title={`${tenant.name} · plan`}
        description="Deneme sırasında da atanır; deneme bitince kesinti yaşanmaz."
        footer={
          <>
            <Button variant="secondary" onClick={() => setAcik(false)}>
              Vazgeç
            </Button>
            <Button type="submit" form={`plan-${tenant.slug}`} disabled={ata.isPending}>
              {ata.isPending ? 'Kaydediliyor…' : 'Kaydet'}
            </Button>
          </>
        }
      >
        <form
          id={`plan-${tenant.slug}`}
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            ata.mutate(new FormData(event.currentTarget))
          }}
        >
          <Field label="Plan" required>
            {(props) => (
              <select
                name="plan_code"
                defaultValue={tenant.plan_code ?? planlar[0]?.code}
                className="h-10 w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-3 text-sm"
                {...props}
              >
                {planlar.map((plan) => (
                  <option key={plan.code} value={plan.code}>
                    {plan.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Ödeme dönemi" required>
            {(props) => (
              <select
                name="billing_cycle"
                defaultValue={tenant.billing_cycle}
                className="h-10 w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-3 text-sm"
                {...props}
              >
                <option value="monthly">Aylık</option>
                <option value="yearly">Yıllık</option>
              </select>
            )}
          </Field>
        </form>
      </Dialog>
    </>
  )
}

/** Abonelik dönemi: geçmişse kaç gün geciktiği, değilse bitiş tarihi. */
function abonelikDurumu(satir: Saglik) {
  if (!satir.subscription_period_end) return <span className="text-xs">—</span>
  const bitis = new Date(satir.subscription_period_end)
  const gun = Math.floor((Date.now() - bitis.getTime()) / 86_400_000)
  if (gun < 0) return <span className="text-xs">{bitis.toLocaleDateString('tr-TR')}</span>
  return <Badge tone={satir.status === 'suspended' ? 'danger' : 'warning'}>{gun} gün gecikti</Badge>
}

/**
 * Ödeme alındı işaretlemesi — YES-176.
 *
 * Askıdaki derneği geri açmanın tek yolu bu servis; sağlayıcı entegrasyonu
 * (YES-177) geldiğinde webhook aynı servisi çağıracak. Buton yalnızca ödeme
 * beklenen derneklerde: aktif bir dernekte "ödeme alındı" demek, sadece
 * dönemi bir ay ileri sarmak olurdu.
 */
function OdemeAlindi({ tenant }: { tenant: Saglik }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const bekleniyor = tenant.status === 'past_due' || tenant.status === 'suspended'

  const isaretle = useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/platform/tenants/{tenant_slug}/subscription/paid', {
        params: { path: { tenant_slug: tenant.slug } },
        body: { period_end: null },
      })
    },
    onSuccess: async () => {
      toast({ title: `${tenant.name} yeniden açıldı`, tone: 'success' })
      await queryClient.invalidateQueries({ queryKey: ['tenants-health'] })
    },
    onError: (error) =>
      toast({
        title: 'İşaretlenemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  if (!bekleniyor) return null
  return (
    <Button
      size="sm"
      variant="secondary"
      disabled={isaretle.isPending}
      onClick={() => {
        if (window.confirm(`${tenant.name} için ödeme alındı olarak işaretlensin mi?`))
          isaretle.mutate()
      }}
    >
      Ödeme alındı
    </Button>
  )
}

function saglikKolonlari(planlar: Plan[]): Column<Saglik>[] {
  return [
    { key: 'name', header: 'Dernek', cell: (row) => row.name, value: (row) => row.name },
    {
      key: 'slug',
      header: 'Alt alan adı',
      cell: (row) => <code className="text-xs">{row.slug}</code>,
      value: (row) => row.slug,
    },
    {
      key: 'status',
      header: 'Durum',
      cell: (row) => (
        <span className="flex flex-col gap-0.5">
          <Badge tone={TONE[row.status]}>{LABEL[row.status]}</Badge>
          {row.status === 'trial' && row.trial_ends_at && (
            <span className="text-xs text-[var(--ad-text-muted)]">
              bitiş {new Date(row.trial_ends_at).toLocaleDateString('tr')}
            </span>
          )}
        </span>
      ),
      value: (row) => row.status,
    },
    {
      key: 'plan',
      header: 'Plan',
      cell: (row) =>
        row.plan_name ? (
          <span>
            {row.plan_name}
            <span className="text-xs text-[var(--ad-text-muted)]">
              {' '}
              · {row.billing_cycle === 'yearly' ? 'yıllık' : 'aylık'}
            </span>
          </span>
        ) : (
          // Plansız dernek hiçbir özellik göremez; bu bir uyarı, boşluk değil.
          <Badge tone="warning">Plan yok</Badge>
        ),
      value: (row) => row.plan_name ?? '',
    },
    {
      key: 'subscription',
      header: 'Abonelik',
      cell: abonelikDurumu,
      value: (row) => row.subscription_period_end ?? '',
    },
    {
      key: 'members',
      header: 'Üye',
      align: 'right',
      cell: (row) => <span className="tabular-nums">{row.member_count}</span>,
      value: (row) => row.member_count,
    },
    {
      key: 'admins',
      header: 'Yönetici',
      align: 'right',
      cell: (row) => (
        <span className="flex items-center justify-end gap-1 tabular-nums">
          {row.admin_count}
          {row.admins_without_mfa > 0 && (
            <Badge tone="warning">{row.admins_without_mfa} 2FA'sız</Badge>
          )}
        </span>
      ),
      value: (row) => row.admin_count,
    },
    {
      key: 'last_login',
      header: 'Son giriş',
      align: 'right',
      cell: (row) =>
        row.last_login_at ? (
          new Date(row.last_login_at).toLocaleDateString('tr')
        ) : (
          <span className="text-[var(--ad-text-muted)]">hiç</span>
        ),
      value: (row) => row.last_login_at ?? '',
    },
    {
      key: 'jobs',
      header: 'Hatalı iş',
      align: 'right',
      cell: (row) =>
        row.unresolved_failed_jobs > 0 ? (
          <Badge tone="danger">{row.unresolved_failed_jobs}</Badge>
        ) : (
          <span className="text-[var(--ad-text-muted)]">0</span>
        ),
      value: (row) => row.unresolved_failed_jobs,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <span className="flex justify-end gap-1">
          <PlanDegistir tenant={row} planlar={planlar} />
          <OdemeAlindi tenant={row} />
          <OlarakGoruntule tenant={{ slug: row.slug, name: row.name } as Tenant} />
        </span>
      ),
    },
  ]
}

/** Örnek liste ekranı: veri tablosu, oluşturma diyaloğu, alan bazlı hata gösterimi. */
export function Dernekler() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Sağlık özeti (YES-169): liste artık "hangi dernekler var" değil "kime
  // bakmam lazım" sorusuna cevap veriyor.
  const { data, isLoading, isError } = useQuery({
    queryKey: ['tenants-health'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/tenants/health')
      // Hatayi YUTMUYORUZ. `data ?? []` yazildiginda 500 donen bir istek
      // ekranda "Henüz dernek yok" olarak görünüyordu: veritabanı boş sanılıyor,
      // asıl arıza görünmez oluyordu. Bir kez pahalıya mal oldu — CI'da
      // AD_SUPERADMIN_DATABASE_URL eksikti ve teşhis bu ekranda kayboldu.
      if (error) throw new Error('Dernek listesi alınamadı')
      return data ?? []
    },
  })
  const planlar = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/platform/plans')
      return data ?? []
    },
  })

  const create = useMutation({
    mutationFn: async (body: { slug: string; name: string }) => {
      const { data } = await api.POST('/api/v1/tenants', { body })
      return data
    },
    onSuccess: async () => {
      setOpen(false)
      setFieldErrors({})
      toast({ title: 'Dernek açıldı', tone: 'success' })
      await queryClient.invalidateQueries({ queryKey: ['tenants-health'] })
    },
    onError: (error: unknown) => {
      // Sunucudan gelen problem+json tek bir hata sınıfına çevriliyor (YES-78).
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors())
        toast({ title: error.problem.title, description: error.problem.detail, tone: 'error' })
        return
      }
      toast({ title: 'Beklenmeyen bir hata oluştu', tone: 'error' })
    },
  })

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    create.mutate({
      slug: String(form.get('slug') ?? ''),
      name: String(form.get('name') ?? ''),
    })
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Dernekler</h1>
        <Button size="sm" onClick={() => setOpen(true)}>
          Dernek ekle
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-[var(--ad-text-muted)]">Yükleniyor…</p>
      ) : isError ? (
        <p role="alert" className="text-sm text-[var(--ad-danger)]">
          Dernek listesi alınamadı. Sayfayı yenileyin; sürerse bu bir sunucu arızasıdır.
        </p>
      ) : (
        <DataTable
          rows={data ?? []}
          columns={saglikKolonlari(planlar.data ?? [])}
          rowKey={(row) => row.slug}
          searchPlaceholder="Dernek ara…"
          exportFileName="dernekler"
          emptyTitle="Henüz dernek yok"
          emptyDescription="İlk derneği açtığınızda burada görünecek."
        />
      )}

      <Duyurular />

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) setFieldErrors({})
        }}
        title="Yeni dernek"
        description="Alt alan adı <slug>.platform.com olarak kullanılır."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Vazgeç
            </Button>
            <Button type="submit" form="dernek-formu" disabled={create.isPending}>
              {create.isPending ? 'Açılıyor…' : 'Aç'}
            </Button>
          </>
        }
      >
        <form id="dernek-formu" onSubmit={submit} className="flex flex-col gap-3">
          <Field label="Dernek adı" required error={fieldErrors.name}>
            {({ id, invalid, required }) => (
              <Input
                id={id}
                name="name"
                invalid={invalid}
                required={required}
                placeholder="BÜTAD Derneği"
              />
            )}
          </Field>
          <Field
            label="Alt alan adı"
            required
            error={fieldErrors.slug}
            hint="Küçük harf, rakam ve tire."
          >
            {({ id, invalid, required }) => (
              <Input
                id={id}
                name="slug"
                invalid={invalid}
                required={required}
                placeholder="butad"
              />
            )}
          </Field>
        </form>
      </Dialog>
    </div>
  )
}

/**
 * Platform duyuruları (YES-169): bütün derneklerin panelinde bant olarak
 * çıkar. Silmek yerine kapatmak öne çıkarılmış; "geçen ay ne duyurmuştuk"
 * sorusunun cevabı listede kalsın.
 */
function Duyurular() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [hatalar, setHatalar] = useState<Record<string, string>>({})

  const duyurular = useQuery({
    queryKey: ['announcements'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/platform/announcements')
      return data ?? []
    },
  })

  function yenile() {
    void queryClient.invalidateQueries({ queryKey: ['announcements'] })
    void queryClient.invalidateQueries({ queryKey: ['announcements-active'] })
  }

  const hataGoster = (error: unknown) => {
    if (error instanceof ApiError) {
      setHatalar(error.fieldErrors())
      toast({ title: error.problem.title, description: error.problem.detail, tone: 'error' })
      return
    }
    toast({ title: 'Beklenmeyen bir hata oluştu', tone: 'error' })
  }

  const yayinla = useMutation({
    mutationFn: async (form: FormData) => {
      const endsAt = String(form.get('ends_at') ?? '')
      await api.POST('/api/v1/platform/announcements', {
        body: {
          title: String(form.get('title') ?? '').trim(),
          body: String(form.get('body') ?? '').trim(),
          tone: form.get('tone') === 'warning' ? 'warning' : 'info',
          // datetime-local yerel saat verir; sunucuya UTC gönderiyoruz.
          ends_at: endsAt ? new Date(endsAt).toISOString() : null,
        },
      })
    },
    onSuccess: () => {
      setOpen(false)
      setHatalar({})
      toast({ title: 'Duyuru yayınlandı', tone: 'success' })
      yenile()
    },
    onError: hataGoster,
  })

  const durumDegistir = useMutation({
    mutationFn: async ({ id, aktif }: { id: string; aktif: boolean }) => {
      await api.PATCH('/api/v1/platform/announcements/{announcement_id}', {
        params: { path: { announcement_id: id } },
        body: { is_active: aktif },
      })
    },
    onSuccess: yenile,
    onError: hataGoster,
  })

  const sil = useMutation({
    mutationFn: async (id: string) => {
      await api.DELETE('/api/v1/platform/announcements/{announcement_id}', {
        params: { path: { announcement_id: id } },
      })
    },
    onSuccess: () => {
      toast({ title: 'Duyuru silindi', tone: 'success' })
      yenile()
    },
    onError: hataGoster,
  })

  return (
    <Card>
      <CardHeader
        title="Duyurular"
        description="Bütün derneklerin panelinde üst bant olarak görünür."
        action={
          <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
            Duyuru yayınla
          </Button>
        }
      />
      <CardBody>
        {duyurular.data && duyurular.data.length === 0 ? (
          <p className="text-sm text-[var(--ad-text-muted)]">Henüz duyuru yok.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-[var(--ad-border)]">
            {(duyurular.data ?? []).map((d) => (
              <li key={d.id} className="flex items-start gap-3 py-3 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{d.title}</span>
                    {d.tone === 'warning' && <Badge tone="warning">Uyarı</Badge>}
                    {duyuruDurumu(d)}
                  </div>
                  <p className="text-xs text-[var(--ad-text-muted)]">{d.body}</p>
                  <p className="text-xs text-[var(--ad-text-muted)]">
                    {new Date(d.starts_at).toLocaleString('tr-TR')}
                    {d.ends_at ? ` → ${new Date(d.ends_at).toLocaleString('tr-TR')}` : ' → süresiz'}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={durumDegistir.isPending}
                  onClick={() => durumDegistir.mutate({ id: d.id, aktif: !d.is_active })}
                >
                  {d.is_active ? 'Kapat' : 'Aç'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={sil.isPending}
                  onClick={() => {
                    if (window.confirm(`"${d.title}" duyurusu silinsin mi?`)) sil.mutate(d.id)
                  }}
                >
                  Sil
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardBody>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) setHatalar({})
        }}
        title="Duyuru yayınla"
        description="Yayınlandığı anda bütün derneklerde görünür."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Vazgeç
            </Button>
            <Button type="submit" form="duyuru-formu" disabled={yayinla.isPending}>
              {yayinla.isPending ? 'Yayınlanıyor…' : 'Yayınla'}
            </Button>
          </>
        }
      >
        <form
          id="duyuru-formu"
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            yayinla.mutate(new FormData(event.currentTarget))
          }}
        >
          <Field label="Başlık" required error={hatalar.title}>
            {(props) => <Input name="title" placeholder="Planlı bakım" {...props} />}
          </Field>
          <Field label="Metin" required error={hatalar.body}>
            {(props) => (
              <Input
                name="body"
                placeholder="Cumartesi 02:00–04:00 arası panel kapalı."
                {...props}
              />
            )}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Tür">
              {({ id }) => (
                <select
                  id={id}
                  name="tone"
                  defaultValue="info"
                  className="h-9 rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-2 text-sm"
                >
                  <option value="info">Bilgi</option>
                  <option value="warning">Uyarı</option>
                </select>
              )}
            </Field>
            <Field label="Bitiş" hint="Boş: elle kapatılana kadar." error={hatalar.ends_at}>
              {(props) => <Input name="ends_at" type="datetime-local" {...props} />}
            </Field>
          </div>
        </form>
      </Dialog>
    </Card>
  )
}

/** Bileşen dışında: saat okumak render'da saf sayılmıyor (react-hooks/purity). */
function duyuruDurumu(d: Duyuru) {
  const simdi = Date.now()
  if (!d.is_active) return <Badge tone="neutral">Kapalı</Badge>
  if (new Date(d.starts_at).getTime() > simdi) return <Badge tone="info">Planlı</Badge>
  if (d.ends_at && new Date(d.ends_at).getTime() <= simdi)
    return <Badge tone="neutral">Bitti</Badge>
  return <Badge tone="success">Yayında</Badge>
}
